import { create } from "zustand";
import type { GlobalSearchState } from "../GlobalSearchWithSDO.types";
import type { SearchFilters } from "@certa/queries/types/globalSearch.types";
import type { ListInteractionMode } from "@certa/catalyst/hooks/useListInteraction";
import type { ReportCustomFilterCondition } from "@certa/types";
import { type SearchResultType } from "../GlobalSearchWithSDO.constants";
import type { ObjectFilterGroup } from "../../ObjectsAdvancedFilter/types";

const initialFilters: SearchFilters = {
  statusIds: [],
  kindIds: [],
  dataObjectTags: [],
  regionIds: [],
  businessUnitIds: [],
  startDate: undefined,
  endDate: undefined,
  objectStatusIds: []
};

const initialState = {
  query: "",
  selectedItem: 0,
  interactionMode: "mouse" as ListInteractionMode,
  selectedCategories: [],
  hasSelectedCategories: false,
  isLoadingWorkflowFamily: false,
  hasSearchQuery: false,
  filters: initialFilters,
  isFilterBarVisible: false,
  isChildDialogOpen: false,
  areAdvancedFiltersVisible: false,
  selectedKindIdForAdvancedFilters: "",
  advancedFilterConditions: [],
  areAdvancedFiltersApplied: false,
  hasPrefilledKinds: false,
  preSelectedKindIds: [],
  hasPrefilledObjectTags: false,
  preSelectedObjectTags: [],
  selectedResultType: null,
  selectedSearchTypeForAdvancedFilters: null,
  selectedDataObjectTagForAdvancedFilters: "",
  dataObjectAdvancedFilters: {
    type: "any" as const,
    conditions: []
  }
};

export const useSearchStore = create<GlobalSearchState>((set, get) => ({
  ...initialState,

  setQuery: (query: string) =>
    set({
      query,
      selectedItem: 0,
      hasSearchQuery: query.trim().length > 0
    }),

  setSelectedItem: selectedItem =>
    set(state => ({
      selectedItem:
        typeof selectedItem === "function"
          ? selectedItem(state.selectedItem)
          : selectedItem
    })),
  setSelectedCategories: categories =>
    set({ selectedCategories: categories, selectedItem: 0 }),
  setHasSelectedCategories: (hasSelectedCategories: boolean) =>
    set({ hasSelectedCategories }),
  setInteractionMode: (interactionMode: ListInteractionMode) =>
    set({ interactionMode }),
  setIsLoadingWorkflowFamily: (isLoading: boolean) =>
    set({ isLoadingWorkflowFamily: isLoading }),
  setFilters: (filters: Partial<SearchFilters>) => {
    set(state => ({
      filters: { ...state.filters, ...filters },
      selectedItem: 0
    }));
  },
  toggleFilterBar: () =>
    set(state => ({ isFilterBarVisible: !state.isFilterBarVisible })),
  resetFilters: () =>
    set(state => ({
      filters: {
        ...initialFilters,
        kindIds: state.filters.kindIds
      },
      selectedItem: 0
    })),
  resetSearch: () =>
    set(state => ({
      selectedItem: 0,
      selectedCategories: [],
      hasSelectedCategories: false,
      selectedKindIdForAdvancedFilters: "",
      areAdvancedFiltersApplied: false,
      selectedSearchTypeForAdvancedFilters: null,
      advancedFilterConditions: [],
      dataObjectAdvancedFilters: {
        type: "any" as const,
        conditions: []
      },
      selectedResultType: null,
      filters: {
        ...initialFilters,
        kindIds: state.preSelectedKindIds,
        dataObjectTags: state.preSelectedObjectTags
      }
    })),
  setIsChildDialogOpen: (isChildDialogOpen: boolean) =>
    set({ isChildDialogOpen }),
  toggleAdvancedFiltersVisibility: () =>
    set(state => ({
      areAdvancedFiltersVisible: !state.areAdvancedFiltersVisible
    })),
  clearAdvancedFilters: () =>
    set({
      selectedSearchTypeForAdvancedFilters: null,
      selectedKindIdForAdvancedFilters: "",
      selectedDataObjectTagForAdvancedFilters: "",
      areAdvancedFiltersApplied: false
    }),
  setSelectedKindIdForAdvancedFilters: (kindId: string) =>
    set({
      selectedSearchTypeForAdvancedFilters: "workflows",
      selectedDataObjectTagForAdvancedFilters: "",
      selectedKindIdForAdvancedFilters: kindId,
      areAdvancedFiltersApplied: kindId !== "",
      advancedFilterConditions: [],
      selectedItem: 0
    }),
  setSelectedDataObjectTagForAdvancedFilters: (tag: string) =>
    set({
      selectedSearchTypeForAdvancedFilters: "dataObjects",
      selectedDataObjectTagForAdvancedFilters: tag,
      selectedKindIdForAdvancedFilters: "",
      areAdvancedFiltersApplied: tag !== "",
      advancedFilterConditions: [],
      selectedItem: 0
    }),
  setDataObjectAdvancedFilters: (filters: ObjectFilterGroup) =>
    set({ dataObjectAdvancedFilters: filters }),
  setAdvancedFilterConditions: (conditions: ReportCustomFilterCondition[]) =>
    set({
      advancedFilterConditions: conditions,
      selectedItem: 0
    }),
  setHasPrefilledKinds: (hasPrefilledKinds: boolean) =>
    set({ hasPrefilledKinds }),
  setPreSelectedKindIds: (preSelectedKindIds: string[]) =>
    set({ preSelectedKindIds }),
  setHasPrefilledObjectTags: (hasPrefilledObjectTags: boolean) =>
    set({ hasPrefilledObjectTags }),
  setPreSelectedObjectTags: (preSelectedObjectTags: string[]) =>
    set({ preSelectedObjectTags }),
  setSelectedResultType: (selectedResultType: SearchResultType | null) =>
    set({ selectedResultType })
}));
